import { Check } from '@phosphor-icons/react';
import type { ImportWizardStep } from '@discovr/features';
import type { ImportBatchRow } from '@discovr/types';
import { cn } from '../../../lib/utils';
import { hadSheetIssues, conflictRows } from './importWizardHelpers';

interface Phase {
  key: 'upload' | 'mapping' | 'resolve-sheet-issues' | 'resolve-roster-conflicts' | 'confirm';
  label: string;
}

const UPLOAD: Phase = { key: 'upload', label: 'Upload' };
const MAPPING: Phase = { key: 'mapping', label: 'Column Mapping' };
const SHEET_REVIEW: Phase = { key: 'resolve-sheet-issues', label: 'Sheet Review' };
const ROSTER_REVIEW: Phase = { key: 'resolve-roster-conflicts', label: 'Roster Review' };
const CONFIRM: Phase = { key: 'confirm', label: 'Confirm' };

/** 'importing'/'done' both read as the terminal Confirm phase — same
 *  grouping the old numeric PHASE_FOR_STEP used. */
function phaseKeyForStep(step: ImportWizardStep): Phase['key'] {
  if (step === 'importing' || step === 'done') return 'confirm';
  return step;
}

interface ImportStepIndicatorProps {
  step: ImportWizardStep;
  /** Sheet Review and Roster Review only appear once the sheet's actually
   *  been classified and shown to need one — before that (Upload/Mapping,
   *  `rows` still empty) the tracker shows just Upload / Column Mapping /
   *  Confirm. Structural, not resolution-based, so a phase already shown
   *  doesn't vanish again once the admin resolves it. */
  rows: ImportBatchRow[];
}

export function ImportStepIndicator({ step, rows }: ImportStepIndicatorProps) {
  const phases: Phase[] = [
    UPLOAD,
    MAPPING,
    ...(hadSheetIssues(rows) ? [SHEET_REVIEW] : []),
    ...(conflictRows(rows).length > 0 ? [ROSTER_REVIEW] : []),
    CONFIRM,
  ];
  const currentIndex = phases.findIndex((p) => p.key === phaseKeyForStep(step));

  return (
    <nav aria-label="Import wizard progress">
      <ol className="m-0 flex list-none items-center gap-2 p-0">
        {phases.map((phase, i) => {
          const done = i < currentIndex;
          const active = i === currentIndex;
          return (
            <li key={phase.key} className="flex items-center gap-2" aria-current={active ? 'step' : undefined}>
              {i > 0 && (
                <span
                  aria-hidden="true"
                  className={cn('h-px w-6 transition-colors duration-200', i <= currentIndex ? 'bg-emerald-400' : 'bg-border')}
                />
              )}
              <div className="flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className={cn(
                    'flex size-5 shrink-0 items-center justify-center rounded-full transition-colors duration-200',
                    done
                      ? 'bg-emerald-500 text-white'
                      : active
                        ? 'bg-primary text-primary-foreground'
                        : 'border border-border',
                  )}
                >
                  {done ? <Check size={11} weight="bold" /> : active ? <span className="size-1.5 rounded-full bg-current" /> : null}
                </span>
                <span
                  className={cn(
                    'text-body-sm-medium',
                    active ? 'font-semibold text-foreground' : done ? 'text-foreground/70' : 'text-muted-foreground',
                  )}
                >
                  {phase.label}
                  {done && <span className="sr-only"> (completed)</span>}
                  {active && <span className="sr-only"> (current step)</span>}
                </span>
              </div>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
